import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Lock, Mail, LogIn, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const AdminLogin = () => {
  const { admin, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (admin) {
    const to = location.state?.from || '/admin';
    return <Navigate to={to} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(location.state?.from || '/admin', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-teal-50/40 px-4 py-12"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        {/* Brand block */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-teal-700 mb-3">
            <ShieldCheck size={20} />
            <span className="text-[11px] font-bold uppercase tracking-widest">WPAA Admin</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Sign in to continue</h1>
          <p className="text-sm text-gray-500 mt-2">
            Secure area — only authorised staff should proceed.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-gray-100 shadow-xl rounded-2xl p-8 space-y-5"
          data-testid="admin-login-form"
        >
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">Email</span>
            <div className="relative mt-1.5">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@wpaauae.com"
                className="pl-9 h-11"
                data-testid="admin-login-email"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-700">Password</span>
            <div className="relative mt-1.5">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-9 h-11"
                data-testid="admin-login-password"
              />
            </div>
          </label>

          {error && (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-semibold"
            data-testid="admin-login-submit"
          >
            {submitting ? 'Signing in…' : (
              <span className="inline-flex items-center gap-2">
                <LogIn size={16} /> Sign in
              </span>
            )}
          </Button>

          <p className="text-[11px] text-gray-400 text-center leading-snug pt-1">
            Five failed attempts trigger a 15-minute lockout from your IP.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
